<template>
    <div class="room">
        <div class="room-content">
            <div class="room-content-number">
                {{ t({ key: 'shared.room', format: LabelFormat.Capitalize }) }}
                {{ roomNumber }}
            </div>

            <div class="room-content-guests">
                {{ roomGuests }}
            </div>

            <div v-if="fullName" class="room-content-pax" v-suppress>
                <span>
                    {{ fullName }}
                    <span v-if="gender"> ({{ gender }}) </span>
                </span>

                <Icon
                    type="InfoCircle"
                    color="black"
                    :tooltip="t({ key: 'checkout.only_a_name_is_necessary' })" />
            </div>
            <div v-if="room.Description" class="room-content-description">
                <TruncateText class="room-note" :text="room.Description" :rows="1" />
            </div>
        </div>

        <SpecialRequest :room="room" />

        <ImportantNotes
            :notes="roomNotes"
            :title="t({ key: 'shared.important_information' })" />
    </div>
</template>
<script lang="ts">
    import { defineComponent, PropType, computed } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { sanitize } from '@shared/utils/sanitize';
    import { isNotNil } from '@shared/utils/value';
    import { Room } from '@/api/Cart/cart.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { getRoomGuests } from '@/helpers/guests';
    import Icon from '@ui/components/Icon/Icon.vue';
    import TruncateText from '@ui/components/TruncateText/TruncateText.vue';
    import SpecialRequest from './SpecialRequest/SpecialRequest.vue';
    import ImportantNotes from '@/components/modals/Variants/Product/Variants/Hotel/Shared/ImportantNotes/ImportantNotes.vue';

    export default defineComponent({
        name: 'HotelModalRooms',
        components: {
            SpecialRequest,
            TruncateText,
            Icon,
            ImportantNotes,
        },
        methods: { sanitize, t },
        props: {
            room: {
                type: Object as PropType<Room>,
                required: true,
            },
            roomNumber: {
                type: Number,
                required: true,
            },
        },
        setup(props) {
            const fullName = computed(() => {
                const { FirstName, LastName } = props.room.Pax;
                return FirstName && LastName ? `${FirstName} - ${LastName}` : undefined;
            });

            const roomGuests = computed(() => getRoomGuests(props.room));

            const gender = computed(() => {
                const { Gender } = props.room.Pax;
                return isNotNil(Gender)
                    ? t({
                          key: `shared.gender_titles.adult.${Gender}`,
                      })
                    : undefined;
            });

            const roomNotes = computed(() =>
                props.room.AllNotes.filter((note) => note.Scope === 'Room')
            );

            return {
                LabelFormat,
                fullName,
                gender,
                roomGuests,
                roomNotes,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Room';
</style>
